import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Stack,
  Tabs,
  Tab,
  Menu,
  MenuItem,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { 
  Search, 
  FilterList, 
  Refresh, 
  Email, 
  Phone,
  CheckCircle,
  Cancel,
  Add,
  Visibility, 
  VisibilityOff,
  Person,
  Lock,
  Edit,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import { useListAdminUsersQuery, useUpdateAdminUserMutation } from '../../features/admin/adminUserListApi.ts';

interface AdminUser {
  id: number;
  full_name: string;
  email: string;
  contact_no: string;
  role: string;
  status: number;
  createddt: string;
  updateddt: string | null;
}

const AdminUserList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });
  
  // Add New User Dialog States
  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    contact_no: "",
    password: "",
    confirm_password: "",
    role: "admin",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [registrationError, setRegistrationError] = useState("");

  // Edit User Dialog States
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    id: 0,
    full_name: "",
    email: "",
    contact_no: "",
    role: "",
    status: 1,
    password: "",
  });
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [editError, setEditError] = useState("");

  const roles = [
    { value: "super_admin", label: "Super Admin" },
    { value: "admin", label: "Admin" },
    { value: "operation", label: "Operation" },
  ];

  // Fetch ALL users without any filter from API
  const { 
    data, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useListAdminUsersQuery({});

  // Update mutation
  const [updateAdminUser, { isLoading: isUpdating }] = useUpdateAdminUserMutation();

  // Handle API response structure
  const adminUsers = data?.data || [];
  const totalRecords = adminUsers.length;

  // Debug logging
  useEffect(() => {
    console.log('API Data:', data);
    console.log('Admin Users:', adminUsers);
    console.log('All Roles in Data:', [...new Set(adminUsers.map(u => u.role))]);
  }, [data, adminUsers]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRefresh = () => {
    refetch();
    setSnackbar({
      open: true,
      message: 'Data refreshed successfully',
      severity: 'success'
    });
  };

  // Add New User Functions
  const handleAddUserClick = () => {
    setAddUserDialogOpen(true);
    // Reset form
    setFormData({
      full_name: "",
      email: "",
      contact_no: "",
      password: "",
      confirm_password: "",
      role: "admin",
    });
    setRegistrationError("");
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleCloseAddUserDialog = () => {
    if (!registrationLoading) {
      setAddUserDialogOpen(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setRegistrationError("");
  };

  const handleRoleChange = (e: any) => {
    setFormData((prev) => ({
      ...prev,
      role: e.target.value,
    }));
    setRegistrationError("");
  };

  // Edit User Functions
  const handleEditClick = (user: AdminUser) => {
    setEditFormData({
      id: user.id,
      full_name: user.full_name,
      email: user.email,
      contact_no: user.contact_no,
      role: user.role,
      status: user.status,
      password: "",
    });
    setEditError("");
    setShowEditPassword(false);
    setEditUserDialogOpen(true);
  };

  const handleCloseEditUserDialog = () => {
    if (!isUpdating) {
      setEditUserDialogOpen(false);
    }
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setEditError("");
  };

  const handleEditRoleChange = (e: any) => {
    setEditFormData((prev) => ({
      ...prev,
      role: e.target.value,
    }));
    setEditError("");
  };

  const handleEditStatusChange = (e: any) => {
    setEditFormData((prev) => ({
      ...prev,
      status: parseInt(e.target.value),
    }));
    setEditError("");
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const validatePassword = (password: string) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setEditError("");

    // Frontend validation
    if (!editFormData.full_name.trim()) {
      setEditError("Full name is required");
      return;
    }

    if (!editFormData.email.trim()) {
      setEditError("Email is required");
      return;
    }

    if (!validateEmail(editFormData.email)) {
      setEditError("Please enter a valid email address");
      return;
    }

    if (!editFormData.contact_no.trim()) {
      setEditError("Contact number is required");
      return;
    }

    if (!validatePhone(editFormData.contact_no)) {
      setEditError("Please enter a valid 10-digit contact number");
      return;
    }

    // Only validate password if it's provided
    if (editFormData.password && editFormData.password.trim() !== '') {
      if (!validatePassword(editFormData.password)) {
        setEditError(
          "Password must be at least 8 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character"
        );
        return;
      }
    }

    if (!editFormData.role) {
      setEditError("Please select a role");
      return;
    }

    try {
      const updateData = {
        id: editFormData.id,
        full_name: editFormData.full_name.trim(),
        email: editFormData.email.trim(),
        contact_no: editFormData.contact_no.trim(),
        role: editFormData.role,
        status: editFormData.status,
        ...(editFormData.password && editFormData.password.trim() !== '' && {
          password: editFormData.password
        })
      };

      const result = await updateAdminUser(updateData).unwrap();

      if (!result.error) {
        // Success
        setSnackbar({
          open: true,
          message: result.message || "Admin user updated successfully!",
          severity: 'success'
        });
        
        // Close dialog
        setEditUserDialogOpen(false);
        
        // Refresh the user list
        refetch();
      } else {
        setEditError(result.message || "Update failed. Please try again.");
      }
    } catch (error: any) {
      console.error("Update error:", error);
      setEditError(error.data?.message || "Update failed. Please try again.");
    }
  };

  const handleRegisterUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegistrationLoading(true);
    setRegistrationError("");

    // Frontend validation
    if (!formData.full_name.trim()) {
      setRegistrationError("Full name is required");
      setRegistrationLoading(false);
      return;
    }

    if (!formData.email.trim()) {
      setRegistrationError("Email is required");
      setRegistrationLoading(false);
      return;
    }

    if (!validateEmail(formData.email)) {
      setRegistrationError("Please enter a valid email address");
      setRegistrationLoading(false);
      return;
    }

    if (!formData.contact_no.trim()) {
      setRegistrationError("Contact number is required");
      setRegistrationLoading(false);
      return;
    }

    if (!validatePhone(formData.contact_no)) {
      setRegistrationError("Please enter a valid 10-digit contact number");
      setRegistrationLoading(false);
      return;
    }

    if (!formData.password.trim()) {
      setRegistrationError("Password is required");
      setRegistrationLoading(false);
      return;
    }

    if (!validatePassword(formData.password)) {
      setRegistrationError(
        "Password must be at least 8 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character"
      );
      setRegistrationLoading(false);
      return;
    }

    if (!formData.confirm_password.trim()) {
      setRegistrationError("Please confirm your password");
      setRegistrationLoading(false);
      return;
    }

    if (formData.password !== formData.confirm_password) {
      setRegistrationError("Passwords do not match");
      setRegistrationLoading(false);
      return;
    }

    if (!formData.role) {
      setRegistrationError("Please select a role");
      setRegistrationLoading(false);
      return;
    }

    const registerURL = `${process.env.REACT_APP_SERVER_ENDPOINT}/admin/register`;
    const authkey = "7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3";

    const requestBody = {
      full_name: formData.full_name.trim(),
      email: formData.email.trim(),
      contact_no: formData.contact_no.trim(),
      password: formData.password,
      confirm_password: formData.confirm_password,
      role: formData.role,
      status: 1,
    };

    try {
      const response = await fetch(registerURL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "authkey": authkey,
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (response.ok && !result.error) {
        // Success - Show snackbar, close dialog, and refresh list
        setSnackbar({
          open: true,
          message: result.message || "Admin user created successfully!",
          severity: 'success'
        });
        
        // Close dialog
        setAddUserDialogOpen(false);
        
        // Refresh the user list to show the newly added user
        refetch();
        
        // Reset form
        setFormData({
          full_name: "",
          email: "",
          contact_no: "",
          password: "",
          confirm_password: "",
          role: "admin",
        });
      } else {
        setRegistrationError(result.message || "Registration failed. Please try again.");
      }
    } catch (error: any) {
      console.error("Registration error:", error);
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        setRegistrationError("Network error: Unable to connect to server. Please check your connection.");
      } else {
        setRegistrationError(`Registration failed: ${error.message}`);
      }
    } finally {
      setRegistrationLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleColor = (role: string) => {
    const roleLower = role?.toLowerCase() || '';
    
    if (roleLower.includes('super')) {
      return { bgcolor: '#fce4ec', color: '#c2185b' };
    }
    if (roleLower === 'admin') {
      return { bgcolor: '#e3f2fd', color: '#1565c0' };
    }
    if (roleLower.includes('operation')) {
      return { bgcolor: '#e8f5e9', color: '#2e7d32' };
    }
    return { bgcolor: '#f5f5f5', color: '#757575' };
  };

  const getRoleLabel = (role: string) => {
    const roleLower = role?.toLowerCase() || '';
    
    if (roleLower.includes('super')) {
      return 'Super Admin';
    }
    if (roleLower === 'admin') {
      return 'Admin';
    }
    if (roleLower.includes('operation')) {
      return 'Operation';
    }
    return role || 'N/A';
  };

  const getStatusColor = (status: number) => {
    return status === 1 
      ? { bgcolor: '#e8f5e9', color: '#2e7d32' }
      : { bgcolor: '#ffebee', color: '#c62828' };
  };

  const getStatusLabel = (status: number) => {
    return status === 1 ? 'Active' : 'Inactive';
  };

  // Get role from tab selection
  const getSelectedRole = () => {
    switch (tabValue) {
      case 1: return 'super_admin';
      case 2: return 'admin';
      case 3: return 'operation';
      default: return ''; // All roles
    }
  };

  // Filter users based on tab selection and search
  const getFilteredUsers = () => {
    let filtered = adminUsers;
    
    // First filter by tab (role)
    const selectedRole = getSelectedRole();
    if (selectedRole) {
      filtered = filtered.filter((user: AdminUser) => {
        const userRole = user.role?.toLowerCase() || '';
        if (selectedRole === 'super_admin') {
          return userRole.includes('super');
        }
        return userRole === selectedRole;
      });
    }
    
    // Then filter by search
    if (searchStr) {
      filtered = filtered.filter((user: AdminUser) => {
        return (
          user.full_name?.toLowerCase().includes(searchStr.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchStr.toLowerCase()) ||
          user.contact_no?.includes(searchStr) ||
          user.role?.toLowerCase().includes(searchStr.toLowerCase())
        );
      });
    }
    
    return filtered;
  };

  const filteredUsers = getFilteredUsers();

  // Count by role - always count from ALL users, not filtered ones
  const roleCounts = {
    all: adminUsers.length,
    super_admin: adminUsers.filter((u: AdminUser) => {
      const role = u.role?.toLowerCase() || '';
      return role.includes('super');
    }).length,
    admin: adminUsers.filter((u: AdminUser) => {
      const role = u.role?.toLowerCase() || '';
      return role === 'admin';
    }).length,
    operation: adminUsers.filter((u: AdminUser) => {
      const role = u.role?.toLowerCase() || '';
      return role.includes('operation');
    }).length,
  };

  console.log('Role Counts:', roleCounts);
  console.log('Filtered Users Count:', filteredUsers.length);
  console.log('Current Tab:', tabValue);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                  Admin Users
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#202223',
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    Refresh
                  </Button>
                  
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddUserClick}
                    sx={{
                      textTransform: 'none',
                      backgroundColor: '#008060',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: '#006e52',
                      }
                    }}
                  >
                    Add New Admin User
                  </Button>
                </Box>
              </Box>

              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                  <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange}
                    sx={{
                      px: 2,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '14px',
                        minHeight: 48,
                        color: '#6d7175',
                      },
                      '& .Mui-selected': {
                        color: '#202223',
                        fontWeight: 600,
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#202223',
                        height: 3,
                      }
                    }}
                  >
                    <Tab label={`All (${roleCounts.all})`} />
                    <Tab label={`Super Admin (${roleCounts.super_admin})`} />
                    <Tab label={`Admin (${roleCounts.admin})`} />
                    <Tab label={`Operation (${roleCounts.operation})`} />
                  </Tabs>
                </Box>

                <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search by name, email, phone, or role"
                    value={searchStr}
                    onChange={handleSearch}
                    fullWidth
                    sx={{ 
                      maxWidth: 500,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        bgcolor: 'white',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: '#6d7175' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    onClick={handleFilterClick}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#202223',
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    Filter
                  </Button>
                  <Menu
                    anchorEl={filterAnchorEl}
                    open={Boolean(filterAnchorEl)}
                    onClose={handleFilterClose}
                  >
                    <MenuItem onClick={() => {
                      setTabValue(0);
                      handleFilterClose();
                    }}>
                      All Roles
                    </MenuItem>
                    <MenuItem onClick={() => {
                      setTabValue(1);
                      handleFilterClose();
                    }}>
                      Super Admin
                    </MenuItem>
                    <MenuItem onClick={() => {
                      setTabValue(2);
                      handleFilterClose();
                    }}>
                      Admin
                    </MenuItem>
                    <MenuItem onClick={() => {
                      setTabValue(3);
                      handleFilterClose();
                    }}>
                      Operation
                    </MenuItem>
                  </Menu>
                </Box>

                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                  </Box>
                )}

                {isError && !isLoading && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Alert severity="error">Failed to load admin users. Please try again.</Alert>
                  </Box>
                )}

                {!isLoading && !isError && filteredUsers.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      No users found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {searchStr ? `No results for "${searchStr}"` : 'No admin users available'}
                    </Typography>
                  </Box>
                )}

                {!isLoading && filteredUsers.length > 0 && (
                  <Box sx={{ bgcolor: 'white' }}>
                    {/* Table Header */}
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: '50px 180px 230px 130px 110px 110px 160px 80px',
                      gap: 2,
                      px: 2,
                      py: 1.5,
                      borderBottom: '1px solid #e1e3e5',
                      bgcolor: '#fafbfb'
                    }}>
                      <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                        No
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                        Full Name
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                        Email
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                        Contact
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                        Role
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                        Status
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                        Created Date
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                        Actions
                      </Typography>
                    </Box>

                    {/* Table Rows */}
                    {filteredUsers.map((user: AdminUser, index: number) => (
                      <Box
                        key={user.id}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '50px 180px 230px 130px 110px 110px 160px 80px',
                          gap: 2,
                          px: 2,
                          py: 2,
                          alignItems: 'center',
                          borderBottom: index < filteredUsers.length - 1 ? '1px solid #e1e3e5' : 'none',
                          '&:hover': { bgcolor: '#f9fafb' },
                          transition: 'background-color 0.15s ease',
                        }}
                      >
                        <Typography variant="body2" sx={{ color: '#6d7175' }}>
                          {index + 1}
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                          {user.full_name || 'N/A'}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Email fontSize="small" sx={{ color: '#6d7175', fontSize: 16 }} />
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={user.email}
                          >
                            {user.email || 'N/A'}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Phone fontSize="small" sx={{ color: '#6d7175', fontSize: 16 }} />
                          <Typography variant="body2" color="text.secondary">
                            {user.contact_no || 'N/A'}
                          </Typography>
                        </Box>

                        <Chip 
                          label={getRoleLabel(user.role)} 
                          size="small" 
                          sx={{ 
                            ...getRoleColor(user.role),
                            fontWeight: 600, 
                            fontSize: '12px',
                            maxWidth: 'fit-content'
                          }} 
                        />

                        <Chip 
                          icon={user.status === 1 ? <CheckCircle sx={{ fontSize: 16 }} /> : <Cancel sx={{ fontSize: 16 }} />}
                          label={getStatusLabel(user.status)} 
                          size="small" 
                          sx={{ 
                            ...getStatusColor(user.status),
                            fontWeight: 600, 
                            fontSize: '12px',
                            maxWidth: 'fit-content'
                          }} 
                        />

                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(user.createddt || '')}
                        </Typography>

                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(user)}
                          sx={{
                            color: '#008060',
                            '&:hover': { bgcolor: 'rgba(0, 128, 96, 0.08)' }
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Card>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Add New Admin User Dialog */}
      <Dialog
        open={addUserDialogOpen}
        onClose={handleCloseAddUserDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '16px',
            maxWidth: '500px'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #e1e3e5',
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Person sx={{ color: '#008060' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
            Add New Admin User
          </Typography>
        </DialogTitle>
        
        <form onSubmit={handleRegisterUser}>
          <DialogContent sx={{ pt: 3 }}>
            {registrationError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {registrationError}
              </Alert>
            )}
            
            <TextField
              label="Full Name"
              name="full_name"
              type="text"
              fullWidth
              value={formData.full_name}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              disabled={registrationLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
              placeholder="Enter full name"
              required
            />

            <TextField
              label="Email Address"
              name="email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              disabled={registrationLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              placeholder="Enter email address"
              required
            />

            <TextField
              label="Contact Number"
              name="contact_no"
              type="text"
              fullWidth
              value={formData.contact_no}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              disabled={registrationLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="action" />
                  </InputAdornment>
                ),
              }}
              placeholder="Enter 10-digit contact number"
              inputProps={{ maxLength: 10 }}
              required
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="role-select-label">Role</InputLabel>
              <Select
                labelId="role-select-label"
                id="role-select"
                value={formData.role}
                label="Role"
                onChange={handleRoleChange}
                disabled={registrationLoading}
              >
                {roles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              fullWidth
              value={formData.password}
              onChange={handleInputChange}
              sx={{ mb: 2 }}
              disabled={registrationLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={registrationLoading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              placeholder="Enter your password"
              required
              helperText="Minimum 8 characters with uppercase, lowercase, number & special character"
            />

            <TextField
              label="Confirm Password"
              name="confirm_password"
              type={showConfirmPassword ? "text" : "password"}
              fullWidth
              value={formData.confirm_password}
              onChange={handleInputChange}
              disabled={registrationLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      disabled={registrationLoading}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              placeholder="Confirm your password"
              required
            />
          </DialogContent>
          
          <DialogActions sx={{ p: 3, borderTop: '1px solid #e1e3e5' }}>
            <Button
              onClick={handleCloseAddUserDialog}
              disabled={registrationLoading}
              sx={{
                textTransform: 'none',
                color: '#202223',
                '&:hover': { backgroundColor: 'transparent' }
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={registrationLoading}
              sx={{
                textTransform: 'none',
                backgroundColor: '#008060',
                '&:hover': { backgroundColor: '#006e52' }
              }}
            >
              {registrationLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                  Creating...
                </>
              ) : (
                'Create Admin User'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Admin User Dialog */}
      <Dialog
        open={editUserDialogOpen}
        onClose={handleCloseEditUserDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: '16px',
            maxWidth: '500px'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #e1e3e5',
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Edit sx={{ color: '#008060' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
            Edit Admin User
          </Typography>
        </DialogTitle>
        
        <form onSubmit={handleUpdateUser}>
          <DialogContent sx={{ pt: 3 }}>
            {editError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {editError}
              </Alert>
            )}
            
            <TextField
              label="Full Name"
              name="full_name"
              type="text"
              fullWidth
              value={editFormData.full_name}
              onChange={handleEditInputChange}
              sx={{ mb: 2 }}
              disabled={isUpdating}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                ),
              }}
              placeholder="Enter full name"
              required
            />

            <TextField
              label="Email Address"
              name="email"
              type="email"
              fullWidth
              value={editFormData.email}
              onChange={handleEditInputChange}
              sx={{ mb: 2 }}
              disabled={isUpdating}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
              placeholder="Enter email address"
              required
            />

            <TextField
              label="Contact Number"
              name="contact_no"
              type="text"
              fullWidth
              value={editFormData.contact_no}
              onChange={handleEditInputChange}
              sx={{ mb: 2 }}
              disabled={isUpdating}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="action" />
                  </InputAdornment>
                ),
              }}
              placeholder="Enter 10-digit contact number"
              inputProps={{ maxLength: 10 }}
              required
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="edit-role-select-label">Role</InputLabel>
              <Select
                labelId="edit-role-select-label"
                id="edit-role-select"
                value={editFormData.role}
                label="Role"
                onChange={handleEditRoleChange}
                disabled={isUpdating}
              >
                {roles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="edit-status-select-label">Status</InputLabel>
              <Select
                labelId="edit-status-select-label"
                id="edit-status-select"
                value={editFormData.status}
                label="Status"
                onChange={handleEditStatusChange}
                disabled={isUpdating}
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="New Password (Optional)"
              name="password"
              type={showEditPassword ? "text" : "password"}
              fullWidth
              value={editFormData.password}
              onChange={handleEditInputChange}
              disabled={isUpdating}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowEditPassword(!showEditPassword)}
                      edge="end"
                      disabled={isUpdating}
                    >
                      {showEditPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              placeholder="Leave blank to keep current password"
              helperText="Minimum 8 characters with uppercase, lowercase, number & special character"
            />
          </DialogContent>
          
          <DialogActions sx={{ p: 3, borderTop: '1px solid #e1e3e5' }}>
            <Button
              onClick={handleCloseEditUserDialog}
              disabled={isUpdating}
              sx={{
                textTransform: 'none',
                color: '#202223',
                '&:hover': { backgroundColor: 'transparent' }
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isUpdating}
              sx={{
                textTransform: 'none',
                backgroundColor: '#008060',
                '&:hover': { backgroundColor: '#006e52' }
              }}
            >
              {isUpdating ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: '#fff' }} />
                  Updating...
                </>
              ) : (
                'Update Admin User'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUserList;
